import { Component } from '@angular/core';
import { BaseFormTableInlineEditComponent } from '../base/base-form-table-inline-edit.component';
import { DataAction } from '../../../enum/data-action.enum';
import { DialogReturnData } from '../../../models/dialog-return-data.model';
import { FormStatus } from '../../../enum/form-status.enum';
import { ModifiedDataStorage } from '../../../models/modified-data-storage.model';
import { MenuItemId } from '../../../enum/menu-item-id.enum';

/**
 * 查詢表單 inline edit 頁面的抽象類別，繼承 BaseFormTableInlineEditComponent
 *
 * @export
 * @abstract
 * @class SimpleDoubleInlineEditComponent
 * @extends {BaseFormTableInlineEditComponent}
 */
@Component({
  template: ''
})
export abstract class SimpleSearchInlineEditComponent extends BaseFormTableInlineEditComponent {

  /**
   * 查詢畫面直接就是編輯狀態。
   */
  override masterFormStatus: FormStatus = FormStatus.EDIT;

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   */
  override hasMasterMenuBar: boolean = false;

  /**
   * 控制查詢條件輸入的 panel 是否縮起。
   */
  panelCollapsed: boolean = false;

  /**
   * search inline edit 的處理不用考慮 master
   */
  override isInlineDependentOnMaster: boolean = false;

  /**
   * inline edit 的刪除都變成清除，因為只是從畫面移除
   */
  // override rowDeleteChangeToClear: boolean = true;

  /**
   * 儲存 inline edit 做的資料異動，在提交時才把這個一次送到後端處理
   */
  modifiedDatas: ModifiedDataStorage = new ModifiedDataStorage();

  /**
   * 修改前的 formGroup rawValue。
   * 取消編輯或重置時會直接用這個把值復原。
   *
   * 如果希望寫程式可以點出提示，可以 override 這個自行定義資料型別。
   */
  originalDetailData: any[] = [];

  /**
   * 編輯明細資料之前是否需要先執行查詢。
   * 預設是 false，如果要先執行查詢才能編輯，就 override 改成 true。
   */
  searchBeforeEditing: boolean = false;

  /**
   * 是否已經成功執行過查詢。
   * 如果 searchBeforeEditing = true，自己要在 doQuery 成功後更新這個值。
   */
  searched: boolean = false;

  /**
   * 重新查詢的 Condition 物件。
   * 每次查詢後都應該把它紀錄下來，當需要重新查詢的時候要直接拿來使用。
   */
  requeryCondition: any;

  /**
   * search inline edit 沒有單頭的 Toolbar。
   * 所以相關的 function 都實作 return。
   */
  override initExtraMasterMenu(): void {
    return;
  }

  /**
   * search inline edit 沒有單頭的 Toolbar。
   * 所以相關的 function 都實作 return。
   */
  override setExtraMasterMenuStatus(): void {
    return;
  }

  /**
   * search inline edit 沒有單頭的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override async beforeMasterMenuDiscard(): Promise<boolean> {
    return true;
  }

  /**
   * search inline edit 沒有單頭的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override async beforeMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  /**
   * search inline edit 沒有單頭的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override async doMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  /**
   * search inline edit 沒有單頭的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override async doMasterMenuDelete(): Promise<boolean> {
    return true;
  }

  /**
   * inline edit 沒有 Row Action 的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override initExtraRowActionMenu(): void {
    return;
  }

  /**
   * inline edit 沒有 Row Action 的按鈕。
   * 所以相關的 function 都實作 return。
   */
  override setExtraRowActionMenuStatus(): void {
    return;
  }

  /**
   * 其實這個不應該在這 override，因為很多程式可能會用到。
   * 在這寫只是為了不影響既有程式。
   * 如果真的有需要請自己再 override 實做。
   */
  override afterDetailMenuStatusChange(): void {
    return;
  }

  /**
   * inline edit 不是開窗編輯。
   * 所以相關的 function 都實作 return。
   */
  override initDialogConfig(dataAction: DataAction): void {
    return;
  }

  /**
   * inline edit 不是開窗編輯。
   * 所以相關的 function 都實作 return。
   */
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }

  /**
   * inline edit 沒有 Row Action 的按鈕。
   */
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }

  /**
   * 刪除資料後，要去判斷刪除的資料有沒有在 modifiedDatas 內。
   *
   * 先把刪除的資料加到 deletedDatas。
   * 如果在 addedDatas 裡面有，代表是新增且尚未存檔到 DB 內，所以刪除後直接拿掉。
   * 如果在 modifiedDatas 裡面有，代表是修改過且尚未更新到 DB 內，所以刪除後也要拿掉。
   *
   * @param deletedData
   */
  override afterDeleteEditableRow(deletedData: any): void {
    if (deletedData.id != DataAction.NEW) {
      this.modifiedDatas.deletedDatas.push(deletedData);
    }

    this.modifiedDatas.addedDatas = this.modifiedDatas.addedDatas.filter(data => {
      return data[this.rowDataKeyName] !== deletedData[this.rowDataKeyName]
    });

    this.modifiedDatas.modifiedDatas = this.modifiedDatas.modifiedDatas.filter(data => {
      return data[this.rowDataKeyName] !== deletedData[this.rowDataKeyName]
    });
  }

  /**
   * 初始化 Table 上方功能選單。
   */
  override initDetailMenu(): void {
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key
    this.detailMenu = [
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_EDIT),
        label: this.translateService.instant(MenuItemId.I18N_EDIT),
        automationId: MenuItemId.I18N_EDIT,
        icon: 'pi pi-fw pi-pencil',
        command: () => {
          this.detailMenuEditCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.DETAIL_MENU_ADD),
        label: this.translateService.instant(MenuItemId.I18N_ADD),
        automationId: MenuItemId.I18N_ADD,
        icon: 'pi pi-fw pi-plus',
        command: () => {
          this.detailMenuAddCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_DISCARD),
        label: this.translateService.instant(MenuItemId.I18N_DISCARD),
        automationId: MenuItemId.I18N_DISCARD,
        icon: 'pi pi-fw pi-times',
        command: () => {
          this.detailMenuDiscardCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_SUBMIT),
        label: this.translateService.instant(MenuItemId.I18N_SUBMIT),
        automationId: MenuItemId.I18N_SUBMIT,
        icon: 'pi pi-fw pi-save',
        command: () => {
          this.detailMenuSubmitCommand();
        }
      }
    ];

    // 是否要顯示 Batch Delete 按鈕
    if (this.canBatchDeleteRows) {
      this.detailMenu.splice(2, 0, {
        id: this.parseToId(MenuItemId.DETAIL_MENU_DELETE),
        label: this.rowDeleteChangeToClear ? this.translateService.instant(MenuItemId.I18N_CLEAR) : this.translateService.instant(MenuItemId.I18N_DELETE),
        automationId: this.rowDeleteChangeToClear ? MenuItemId.I18N_CLEAR : MenuItemId.I18N_DELETE,
        icon: this.rowDeleteChangeToClear ? 'pi pi-fw pi-minus' : 'pi pi-fw pi-trash',
        command: () => {
          this.detailMenuBatchDeleteCommand();
        }
      });
    }

    if (this.hasTableFilter) {
      this.detailMenu.push({
        id: this.parseToId(MenuItemId.DETAIL_MENU_FILTER),
        label: this.translateService.instant(MenuItemId.I18N_FILTER),
        automationId: MenuItemId.I18N_FILTER,
        icon: 'pi pi-fw pi-filter',
        command: () => {
          this.detailMenuFilterCommand();
        }
      });
    }

    this.setDetailMenuStatus();
  }

  override afterMasterMenuStatusChange(): void {
    // 單頭狀態改變的時候把 detail 設成 VIEW
    // 因為單頭一進來就只會是 EDIT 狀態，所以這裡是設定一進來單身是瀏覽狀態，要按編輯才能異動
    this.detailFormStatus = FormStatus.VIEW;
    this.setDetailMenuStatus();
  }

  /**
   * 設定 Table 上方功能選單各按鈕的狀態。
   */
  override setDetailMenuStatus(): void {
    let detailMenuEdit = this.getDetailMenuItem(MenuItemId.MASTER_MENU_EDIT);
    if (detailMenuEdit) {
      // 瀏覽狀態和篩選狀態不能按編輯
      // 如果 searchBeforeEditing = true，searched = false，代表沒先查詢，也不能按
      detailMenuEdit.disabled = !this.detailInViewStatus() || (this.searchBeforeEditing && !this.searched);
    }

    let detailMenuDelete = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_DELETE);
    if (detailMenuDelete) {
      // 瀏覽狀態和沒有選擇資料不能按清除
      detailMenuDelete.disabled = this.detailInViewStatus() || (this.rowSelectedDatas && this.rowSelectedDatas.length <= 0);
      detailMenuDelete.visible = !this.detailInViewStatus();
    }

    let detailMenuAdd = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_ADD);
    if (detailMenuAdd) {
      // 瀏覽狀態和篩選狀態不能按新增
      detailMenuAdd.disabled = this.detailInViewStatus() || this.tableFilterShow;
      detailMenuAdd.visible = !this.detailInViewStatus();
    }

    let detailMenuDiscard = this.getDetailMenuItem(MenuItemId.MASTER_MENU_DISCARD);
    if (detailMenuDiscard) {
      // 瀏覽狀態和篩選狀態不能按新增
      detailMenuDiscard.disabled = this.detailInViewStatus();
      detailMenuDiscard.visible = !this.detailInViewStatus();
    }

    let detailMenuSubmit = this.getDetailMenuItem(MenuItemId.MASTER_MENU_SUBMIT);
    if (detailMenuSubmit) {
      // 瀏覽狀態和篩選狀態不能按新增
      detailMenuSubmit.disabled = this.detailInViewStatus();
      detailMenuSubmit.visible = !this.detailInViewStatus();
    }

    let detailMenuFilter = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_FILTER);
    if (detailMenuFilter) {
      detailMenuFilter.disabled = false;
    }

    this.setExtraDetailMenuStatus();
    this.redrawDetailMenu();
    this.afterDetailMenuStatusChange();
  }

  /**
   * 功能選單 - Edit 按鈕按下去要做的事件
   */
  private detailMenuEditCommand(): void {
    // 把單身狀態設為編輯狀態
    this.detailFormStatus = FormStatus.EDIT;
    // 重繪 Table 功能列
    this.setDetailMenuStatus();
    // 進入編輯以後把當下的資料寫入 originalDetailData
    this.originalDetailData = [...this.detailDatas];
  }

  /**
   * 功能選單 - Discard 按鈕按下去要做的事件
   */
  private async detailMenuDiscardCommand(): Promise<boolean> {
    return new Promise(resolve => {
      this.systemConfirmationService.confirmDiscardChanges(() => {
        if (this.isTableInEdit()) {
          const btnCancelEditableRow = this.elementRef.nativeElement.querySelector('#' + this.parseToId('btnCancelEditableRow'));
          if (btnCancelEditableRow) {
            btnCancelEditableRow.click();
          }
        }

        // 單身狀態切回瀏覽狀態
        this.detailFormStatus = FormStatus.VIEW;

        // 重繪 Table 功能列
        this.setDetailMenuStatus();

        // 先把欄位都清空
        this.inlineFormGroup.reset();

        // 復原單身資料
        this.detailDatas = [...this.originalDetailData];

        // modifiedDatas 清空
        this.modifiedDatas = new ModifiedDataStorage();

        // 清除 Table 多選選擇的資料
        this.clearRowSelectedDatas();
      });
    });
  }

  /**
   * 功能選單 - Submit 按下去後要先做的事情
   * 存檔前要先做 inline edit 的驗證與儲存
   */
  private async beforeDetailMenuSubmit(): Promise<boolean> {
    if (this.isTableInEdit()) {
      const checked = await this.saveEditingRow();
      return Promise.resolve(checked.success);
    } else {
      return true;
    }
  }

  /**
   * 功能選單 - Submit 按下去預設後要做的事情。
   * (1) 先做前處理過了才繼續。
   * (2) 進行欄位驗證。
   * (3) 欄位驗證過後，顯示處理中遮罩。
   * (4) 呼叫 doDetailMenuSubmit()，處理實際送出要做的事情。
   * (5) 處理完後呼叫 detailMenuSubmittedCallback()。
   */
  private async detailMenuSubmitCommand(): Promise<any> {
    const canDoSave = await this.beforeDetailMenuSubmit();
    if (!canDoSave) {
      return;
    }

    this.loadingMaskService.show();
    this.inlineSubmitted = true;

    // 如果 inline edit 有正在編輯中的資料，要先確認資料沒有錯誤才繼續
    // 或是 inline edit 沒有正在編輯中的資料
    if ((this.isTableInEdit() && this.inlineFormGroup.valid) || !this.isTableInEdit()) {
      this.waitAutoCompleteCompleted(
        async() => {
          // complete 後要實際去執行 doDetailMenuSubmit()
          const submitted = await this.doDetailMenuSubmit();
          if (submitted) {
            this.afterDetailMenuSubmitted();
          }
          this.loadingMaskService.hide();
        }
      );
    } else {
      this.loadingMaskService.hide();
    }
  }

  /**
   * 要自己實作實際 Submit 的事件。
   */
  abstract doDetailMenuSubmit(): Promise<boolean>;

  /**
   * 功能選單 - Submit 完畢後要做的事情。
   * (1) 狀態切回瀏覽狀態。
   * (2) 移除處理中遮罩。
   * (3) 顯示新增或修改成功訊息。
   */
  private afterDetailMenuSubmitted(): void {
    // 把資料空白的的 id 設成跟 uuid 一樣，因為存檔後資料不會重查
    // 避免再次進入編輯被當作新增資料處理
    this.detailDatas.forEach(data => {
      if (data.id === '') {
        data.id = data.uuid;
      }
    });

    // 狀態切回瀏覽狀態
    this.detailFormStatus = FormStatus.VIEW;

    // modifiedDatas 清空
    this.modifiedDatas = new ModifiedDataStorage();

    // 重繪 Table 功能列
    this.setDetailMenuStatus();

    // 清除 Table 多選選擇的資料
    this.clearRowSelectedDatas();

    this.loadingMaskService.hide();

    this.systemMessageService.success('message.detail.update-success');
  }

  /**
   * 需要在查詢前做的事情，可以在這做一些額外的驗證。
   * 如果有做額外的驗證要配合 formGroup 的 valid，不然驗證到有錯還是會繼續執行查詢。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract beforeQuery(): Promise<boolean>;

  /**
   * 按下查詢按鈕要做的動作。
   */
  async query(): Promise<void> {
    // 判斷單身是否在編輯狀態
    if (!this.detailInViewStatus()) {
      // 如果是在編輯狀態要先放棄才能做查詢
      const discard = await this.detailMenuDiscardCommand();
      if (discard) {
        this.queryCommand();
      }
    } else {
      this.queryCommand();
    }
  }

  /**
   * 查詢按鈕實際要做的動作。
   * (1) 呼叫 beforeQuery() 做事前的處理。
   * (2) 進行欄位驗證。
   * (3) 欄位驗證過後，顯示處理中遮罩。
   * (4) 清除 Table 多選選擇的資料。
   * (5) 確定所有 autoAomplete 欄位都做完驗證才呼叫 doQuery() 進行查詢。
   * (6) 處理完後呼叫 doQueryCallback()。
   */
  private async queryCommand(): Promise<void> {
    this.loadingMaskService.show();
    const canQuery = await this.beforeQuery();

    if (!canQuery) {
      this.loadingMaskService.hide();
      return;
    }

    this.panelCollapsed = false;
    this.submitted = true;
    if (this.formGroup.valid) {
      // 清除已選擇 Row
      this.clearRowSelectedDatas();

      // 把已經查詢設回 false，如果 searchBeforeEditing = true，請各自程式一定要在 doQuery 成功後
      // 自己把 searched 更新成 true，重要!!
      this.searched = false;

      this.waitAutoCompleteCompleted(
        async() => {
          // complete 後要實際去執行 doQuery()
          const queried = await this.doQuery();
          if (queried) {
            this.afterQuery();
          }
        }
      );
    } else {
      this.loadingMaskService.hide();
    }
  }

  /**
   * 要自己實作實際查詢做的事情。
   */
  abstract doQuery(requeryCondition?: any): Promise<boolean>;

  /**
   * 查詢完畢後要做的事情。
   * (1) 移除處理中遮罩。
   */
  afterQuery(): void {
    // 切回第一頁
    // setTimeout 0 是重點，不然頁數顯示有可能會停在上一次的查詢結果
    setTimeout(() => {
      this.tableComponent.first = 0;

      // 重繪 Table 功能列
      this.setDetailMenuStatus();
    }, 0);
    this.submitted = false;
    this.loadingMaskService.hide();
  }

  /**
   * 清除 searchForm，還原原始狀態。
   */
  async searchReset(): Promise<void> {
    let discard = true;
    // 判斷單身是否在編輯狀態
    if (!this.detailInViewStatus()) {
      // 如果是在編輯狀態要先放棄才能做清除
      discard = await this.detailMenuDiscardCommand();
    }

    if (discard) {
      this.doReset();
    }
  }

  /**
   * 執行 Reset
   */
  private doReset(): void {
    // 清空 Detail 資料
    this.clearDetail();
    // 清空查詢條件
    this.formGroup.reset();
    // 還原畫面開啟時載入的預設值
    this.formGroup.patchValue(this.originalFormRawValue);
    this.submitted = false;
  }

  /**
   * 清空 Detail 資料
   */
  clearDetail(): void {
    // 清除已選擇 Row
    this.clearRowSelectedDatas();

    // 清除查詢結果
    this.detailDatas = [];

    this.searched = false;

    // 重繪 Table 功能列
    this.setDetailMenuStatus();
  }

  /**
   * 把資料更新回 detail 和 modifiedDatas。
   * 寫在 doSaveEditableRow 裡面拚好 savedData 後。
   *
   * @param savedData inline edit 維護得當筆資料
   * @returns 更新是否成功
   */
  async updateDetailAndModifiedDatas(savedData: any): Promise<boolean> {
    const indexToUpdate = this.detailDatas.findIndex(data => data[this.rowDataKeyName] === savedData[this.rowDataKeyName]);
    this.detailDatas[indexToUpdate] = savedData;
    this.detailDatas = [...this.detailDatas];

    // 如果資料有改過
    if (!this.inlineFormGroup.pristine) {
      // 要根據資料是新增還是刪除，先把資料更新到 modifiedDatas 裡
      // 要取 modifiedDatas 裡面的那一個屬性出來檢查並塞資料
      let datasObject: any[] = [];
      if (this.inlineFormGroup.getRawValue().id == DataAction.NEW || this.inlineFormGroup.getRawValue().id == '') {
        // 如果 id 是 new (第一次新增)，或是 id 是空的 (新增)
        datasObject = this.modifiedDatas.addedDatas;
      } else {
        // 修改的資料
        datasObject = this.modifiedDatas.modifiedDatas;
      }

      const existingDataIndex = datasObject.findIndex(data => data[this.rowDataKeyName] === savedData[this.rowDataKeyName]);
      if (existingDataIndex > -1) {
        // 如果在 modifiedDatas 裡已經有塞過資料了，代表是重複改了同一筆，要更新
        datasObject[existingDataIndex] = savedData;
      } else {
        // 找不到就是新異動的資料，用 push 加入
        datasObject.push(savedData);
      }
    }
    return true;
  }

  /**
   * Serach Inline 的批次刪除前要先組好 modifiedDatas
   */
  override async beforeDetailMenuBatchDelete(): Promise<boolean> {
    // 將 this.rowSelectedDatas id 不等於 DataAction.NEW 加入到 this.modifiedDatas.deletedDatas 陣列中
    this.modifiedDatas.deletedDatas.push(...this.rowSelectedDatas.filter((data: { id: DataAction; }) => data.id !== DataAction.NEW));

    // 將 this.rowSelectedDatas 資料從 this.modifiedDatas.addedDatas 陣列中過濾掉
    this.modifiedDatas.addedDatas = this.modifiedDatas.addedDatas.filter(data => {
      return !this.rowSelectedDatas.some((selectedData: { [x: string]: any; }) => selectedData[this.rowDataKeyName] === data[this.rowDataKeyName]);
    });

    // 將 this.rowSelectedDatas 資料從 this.modifiedDatas.modifiedDatas 陣列中過濾掉
    this.modifiedDatas.modifiedDatas = this.modifiedDatas.modifiedDatas.filter(data => {
      return !this.rowSelectedDatas.some((selectedData: { [x: string]: any; }) => selectedData[this.rowDataKeyName] === data[this.rowDataKeyName]);
    });

    return true;
  }

  /**
   * 為了避免影響既有程式，預設不提供批次刪除
   * 有需要的再自己 override
   */
  override async doDetailMenuBatchDelete(): Promise<boolean> {
    return true;
  }

  /**
   * 重新查詢。
   * 通常會用在查詢出來的資料有異動的時候呼叫，用前一次查詢的條件重新查詢。
   */
  async requery(): Promise<void> {
    this.loadingMaskService.show();
    // 清除已選擇 Row
    this.clearRowSelectedDatas();
    const queried = await this.doQuery(this.requeryCondition);
    if (queried) {
      this.afterQuery();
    }
  }

  override async beforeSaveEditableRow(): Promise<boolean> {
    return true;
  }
}
